import { EventEmitter, Injectable, inject } from '@angular/core';
import { ApolloClient } from '@apollo/client';
import { from, map, Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { getUserBusinessUnits } from '../../graphql/contacts/get-user-business-units';
import { loadUserClaims } from '../../graphql/contacts/load-user-claims';
import { BusinessUnit } from '../../model/admin/business-unit';
import { Claim } from '../../model/common/user-claim';
import { sortByProperty } from '../../utils/utils';
import { AppsyncService } from '../appsync.service';
import { AuthService } from '../auth.service';
import { getGraphQLField } from '../../graphql/graphql-response';

interface UserClaimsData {
  loadUserClaims: Claim[] | null;
}

interface UserBusinessUnitsData {
  getUserBusinessUnits: BusinessUnit[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  protected _api = inject(AppsyncService);
  protected _auth = inject(AuthService);

  MINUTES_FOR_CLAIM_CACHE_REFRESH: number = 5;

  userClaims: Claim[] = [];
  userClaimsLastUpdate: Date = new Date(1970, 0, 1);
  userClaimsUpdated: EventEmitter<any> = new EventEmitter();

  userBusinessUnits: BusinessUnit[] = [];

  getUserClaims() {
    const now = new Date();

    if (
      now.getTime() - this.userClaimsLastUpdate.getTime() <
      this.MINUTES_FOR_CLAIM_CACHE_REFRESH * 60 * 1000
    ) {
      // we polled the claims within the last MINUTES_FOR_CLAIM_CACHE_REFRESH minutes,
      // so we don't need to re query the API here
      return this.userClaims;
    } else {
      this.loadUserClaims()
        .pipe(take(1))
        .subscribe((response) => {
          let claimResponse = response.data.loadUserClaims;
          if (claimResponse !== null) {
            this.setUserClaims(claimResponse);
          }
        });
      return this.userClaims;
    }
  }

  getUserClaimsHeader() {
    return this.userClaims?.map((x) => x.name).join(',');
  }

  loadUserClaims(): Observable<
    ApolloClient.QueryResult<UserClaimsData> & { data: UserClaimsData }
  > {
    return from(this._api.gqlClient()).pipe(
      switchMap((client) => {
        const options: ApolloClient.QueryOptions<UserClaimsData> = {
          query: loadUserClaims,
          fetchPolicy: 'no-cache',
        };
        return client.query<UserClaimsData>(options);
      }),
      map((result) => {
        const claims = getGraphQLField(result.data, 'loadUserClaims');
        return { ...result, data: { ...result.data, loadUserClaims: claims } };
      })
    );
  }

  setUserClaims(claims: Claim[]) {
    this.userClaims = claims;
    this.userClaimsLastUpdate = new Date();
    this.userClaimsUpdated.emit();
  }

  doesUserHaveAtLeastOneClaimFromList(claimNames: string[]): boolean {
    let userClaimNames = this.userClaims?.map((x) => x.name);
    if (claimNames.some((claimName) => userClaimNames?.includes(claimName))) {
      return true;
    }

    return false;
  }

  doesUserHaveClaim(claim: string): boolean {
    return this.doesUserHaveAllClaimsFromList([claim]);
  }

  doesUserHaveAllClaimsFromList(claimNames: string[]): boolean {
    let userClaimNames = this.userClaims?.map((x) => x.name);
    if (claimNames.every((claimName) => userClaimNames?.includes(claimName))) {
      return true;
    }

    return false;
  }

  getUserBusinessUnits(): BusinessUnit[] {
    return this.userBusinessUnits;
  }

  loadUserBusinessUnits() {
    from(this._api.gqlClient())
      .pipe(
        switchMap((client) => {
          const options: ApolloClient.QueryOptions<UserBusinessUnitsData> = {
            query: getUserBusinessUnits,
            fetchPolicy: 'no-cache',
          };
          return client.query<UserBusinessUnitsData>(options);
        })
      )
      .pipe(take(1))
      .subscribe((result) => {
        this.userBusinessUnits = getGraphQLField(
          result.data,
          'getUserBusinessUnits'
        ).sort((a: BusinessUnit, b: BusinessUnit) =>
          sortByProperty(a, b, 'name')
        );
      });
  }
}
